import {Close, Lock, LockOpen, Visibility, VisibilityOff} from '@mui/icons-material';
import {Autocomplete, Box, Button, Divider, IconButton, MenuItem, TextField} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { GridLinkOperator } from '@mui/x-data-grid-pro';
import React, {useCallback, useContext} from 'react';
import { connect } from 'react-redux';
import { selectModeData, selectProject } from '../../redux/selectors/editor';
import { selectConstants, selectSheets } from '../../redux/selectors/firebase';
import { FirebaseContext } from '../firebase/FirebaseProvider';
import { Constants } from '../graph/StateTags';
import { Form } from '../input/Form';
import { TextFieldAuto } from '../input/TextFieldAuto';
import {ColumnDetails, ConstantColumnData, getColumnDetails} from './gridColumnDefs';
import {IGridFilterItem, Sheets} from './GridView';
import {ISheetModeData} from "../../redux/slices/editor";

interface Props
{
    project: string
    modeData: ISheetModeData
    sheets: Sheets
    constants: Constants
}

const useStyles = makeStyles((theme) => ({
    addButton: { margin: theme.spacing(1) },
    linkOperator: {
        "& .MuiSelect-select": {
            paddingTop: theme.spacing(2)
        }
    },
    input: {
        marginRight: 1
    },
    icon: {
        display: "inline-block",
        "& svg": {
            width: "0.8em",
            height: "0.8em",
        },
        marginRight: theme.spacing(1)
    },
    form: {
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        marginBottom: theme.spacing(1)
    }
}));

const operators = {
    common: {
        isEmpty: "is empty",
        isNotEmpty: "is not empty"
    },
    string: {
        equals: "equals",
        contains: "contains",
        doesNotContain: "does not contain",
        startsWith: "starts with",
        endsWith: "ends with",
        isAnyOf: "is any of"
    },
    number: {
        "=": "=",
        "!=": "!=",
        "<": "<",
        "<=": "<=",
        ">": ">",
        ">=": ">=",
    },
    boolean: {
        "is": "is"
    },
    date: {
        "is": "is",
        "not": "is not",
        "after": " is after",
        "onOrAfter": "is on or after",
        "before": "is before",
        "onOrBefore": "is on or before",
        "beforeColumn": "Is before column",
        "afterColumn": "Is after column",
    },
    dateTime: {
        "is": "is",
        "not": "is not",
        "after": " is after",
        "onOrAfter": "is on or after",
        "before": "is before",
        "onOrBefore": "is on or before",
        "beforeColumn": "Is before column",
        "afterColumn": "Is after column",
    },
    singleSelect: {
        "is": "is",
        "not": "is not",
        "isAnyOf": "is any of"
    }
}

type KeyOfOperators = keyof typeof operators;

function SheetFilters({ project, modeData, sheets, constants }: Props)
{
    const classes = useStyles();
    const { sheetId } = modeData;

    const { set, push, remove } = useContext(FirebaseContext);
    const sheet = sheets[sheetId ?? ""];
    const filterModel = sheet?.filterModel?.items ? sheet.filterModel : { items: {}, linkOperator: GridLinkOperator.And };
    const columns = Object.entries(sheet?.columns ?? []);

    const setFilter = useCallback((filterId: string, filterItem: IGridFilterItem) =>
    {
        if (filterItem.value === undefined)
            delete filterItem.value;

        if (filterItem.operatorValue === undefined)
            delete filterItem.value;

        set(`sheets/${project}/${sheetId}/filterModel/items/${filterId}`, filterItem);
    }, [set, project, sheetId]);

    const handleClickAddFilter = useCallback(() =>
    {
        const columnDetails = getColumnDetails(columns[0], constants);

        if (columnDetails)
        {
            push(`sheets/${project}/${sheetId}/filterModel/items/`, { columnField: columnDetails.field });
        }
    }, [columns, constants, push, project, sheetId]);

    const handleClickDeleteFilter = useCallback((event: React.MouseEvent<HTMLElement>) =>
    {
        const filterId = event.currentTarget.dataset.id;
        if (filterId)
        {
            remove(`sheets/${project}/${sheetId}/filterModel/items/${filterId}`);
        }
    }, [project, remove, sheetId]);

    const createEntries = (key: KeyOfOperators) =>
    {
        const entries = operators[key];
        return [<Divider key={key} />].concat(Object.entries(entries).map(([id, text]) =>
        {
            return <MenuItem key={`${key}_${id}`} value={id}>{text}</MenuItem>
        }));
    }

    const getOperators = (details?: ColumnDetails) =>
    {
        const items = createEntries("common");

        if (details && details.type in operators)
        {
            items.push(...createEntries(details.type as KeyOfOperators));
        }

        return items
    }

    const hasValue = (operator: string) =>
    {
        const typesWithValue: KeyOfOperators[] = [
            "number",
            "string",
            "boolean",
            "date",
            "dateTime",
            "singleSelect"
        ];
        return typesWithValue.some((type) => operator in operators[type])
    }

    const valueInput = (details: ColumnDetails | undefined, onChange: (value: any) => void, operator?: string, value?: any) =>
    {
        if (details && operator && hasValue(operator))
        {
            switch (details.type)
            {
                case "boolean":
                    return <TextField label={"Value"}
                        variant={"outlined"}
                        size={"small"}
                        margin={"dense"}
                        fullWidth
                        select
                        SelectProps={{
                            value: value ?? "is",
                            onChange: (e) => { onChange(e.target.value); }
                        }}>
                        <MenuItem value={"any"}>any</MenuItem>
                        <MenuItem value={"true"}>true</MenuItem>
                        <MenuItem value={"false"}>false</MenuItem>
                    </TextField>
                case "singleSelect":
                    if (operator === "isAnyOf")
                    {
                        return (
                            <Autocomplete multiple limitTags={3} options={Object.entries(details.valueOptions ?? {}).map(([id, option]) => ({ label: option, id }))}
                                value={Array.isArray(value) ? value.map((id) => ({ id, label: details?.valueOptions && details.valueOptions[id] })) : []}
                                isOptionEqualToValue={(a, b) => a.id === b.id}
                                onChange={(e, value) => onChange(value.map((entry) => entry.id))}
                                renderInput={(params) =>
                                    <TextField {...params}
                                        variant={"outlined"}
                                        fullWidth
                                        size={"small"}
                                        margin={"dense"}
                                        label="Value" />}
                            />
                        );
                    }
                    return <TextField label={"Value"}
                        variant={"outlined"}
                        size={"small"}
                        margin={"dense"}
                        fullWidth
                        select
                        SelectProps={{
                            value: value ?? "is",
                            onChange: (e) => { onChange(e.target.value); }
                        }}>
                        {Object.entries(details.valueOptions ?? {}).map(([id, option]) => <MenuItem key={id} value={id}>{option}</MenuItem>)}
                    </TextField>
                case "date":
                case "dateTime":
                    if (operator === "beforeColumn" || operator === "afterColumn") {
                        const options: {[key: string]: string} = Object.fromEntries(Object.entries(sheet.columns ?? {})
                            .map(([key, column]) => {
                                const constantColumnData = column as ConstantColumnData;

                                if (constantColumnData.source) {
                                    const constant = constants[constantColumnData.source];

                                    return [constantColumnData.source, constant?.name]
                                }
                                return [key, undefined];
                            })
                            .filter(([key]) => key !== details.field)
                            .filter(([, name]) => name !== undefined));

                        return (
                            <TextField label="Value" variant="outlined" size="small" margin="dense" fullWidth select
                                       onChange={({target}) => onChange(target.value)} value={value ?? ""}>
                                <MenuItem value="" disabled>Select Column</MenuItem>
                                {Object.entries(options).map(([key, name]) =>
                                    <MenuItem key={key} value={key}>{name}</MenuItem>
                                )}
                            </TextField>
                        );
                    } else {
                        return <TextField InputLabelProps={{ shrink: true }} label="Value" variant="outlined" size="small"
                                          margin="dense" type="datetime-local" fullWidth value={value ?? ""}
                                          onChange={(e) => onChange(e.currentTarget.value)}/>
                    }
                default:
                    if (operator === "isAnyOf")
                    {
                        return (
                            <Autocomplete multiple limitTags={3} options={[]} freeSolo
                                value={Array.isArray(value) ? value : []}
                                onChange={(e, value) => {
                                    onChange(value)
                                }}
                                renderInput={(params) =>
                                    <TextField {...params}
                                        variant={"outlined"}
                                        fullWidth
                                        size={"small"}
                                        margin={"dense"}
                                        label="Value" />}
                            />
                        );
                    }
                    return <TextFieldAuto
                        label={"Value"}
                        variant={"outlined"}
                        size={"small"}
                        margin={"dense"}
                        fullWidth
                        value={value ?? ""}
                        onCommit={onChange} />
            }
        }
        return null;
    }
    const handleToggleMember = (filterId: string, member: "inactive" | "locked") => {
        return () => {
            const filter = filterModel.items[filterId];

            set(`sheets/${project}/${sheetId}/filterModel/items/${filterId}`, {
                ...filter,
                [member]: ! filter[member]
            }).catch(console.error);
        }
    }

    return <Box>
        {Object.entries(filterModel.items).map(([id, filter]) =>
        {
            const selectedColumn = columns.map((entry) =>
                getColumnDetails(entry, constants) ?? undefined).find((details) => details?.field === filter.columnField)

            return <Box key={id}>
                <Form className={classes.form}>
                    <Box flexGrow={1} display="flex">
                        <Box flexDirection="column" flexGrow={1} display="flex">
                            <Box flexGrow={1} display="flex">
                                <Box flexGrow={1} className={classes.input}>
                                    <TextFieldAuto label={"Column"} variant={"outlined"} size={"small"} margin={"dense"}
                                        fullWidth select SelectProps={{
                                            fullWidth: true,
                                            value: filter.columnField,
                                            onChange: (e) =>
                                            {
                                                const value = e.target.value as string;
                                                setFilter(id, { columnField: value })
                                            },
                                        }}>
                                        {columns.map(([id, column], index) =>
                                        {
                                            const details = getColumnDetails([id, column], constants);
                                            return <MenuItem key={index} value={details?.field}>
                                                <Box className={classes.icon}>{details?.icon ?
                                                    React.createElement(details.icon) : null}</Box> {details?.name}
                                            </MenuItem>
                                        })}
                                    </TextFieldAuto>
                                </Box>
                            </Box>
                            <Box display="flex" flexGrow={1}>
                                <Box flexGrow={1} className={classes.input}>
                                    <TextFieldAuto label={"Operator"} variant={"outlined"} size={"small"} margin={"dense"}
                                        fullWidth select SelectProps={{
                                            value: filter.operatorValue ?? "isEmpty",
                                            onChange: (e) =>
                                            {
                                                setFilter(id, {
                                                    ...filterModel.items[id],
                                                    operatorValue: e.target.value as string
                                                })
                                            }
                                        }}>
                                        {getOperators(selectedColumn)}
                                    </TextFieldAuto>
                                </Box>
                                <Box flexGrow={1} marginLeft={1} className={classes.input}>
                                    {valueInput(selectedColumn, (value: any) =>
                                    {
                                        setFilter(id, {
                                            ...filterModel.items[id],
                                            value
                                        })
                                    }, filter.operatorValue, filter.value)}
                                </Box>
                                {selectedColumn?.type === "string" &&
                                    <Box sx={{marginTop: 1}}>
                                        <Button onClick={() => setFilter(id, {
                                            ...filterModel.items[id],
                                            caseSensitive: !filterModel.items[id].caseSensitive
                                        })} sx={{minHeight: 40, minWidth: 40, padding: 0, textTransform: "none"}}
                                                title={`Toggle case sensitivity (currently ${filterModel.items[id].caseSensitive ? "on" : "off"})`}
                                                variant={filterModel.items[id].caseSensitive ? "outlined" : "text"}>
                                            Aa
                                        </Button>
                                    </Box>
                                }
                            </Box>
                        </Box>
                        <Box flexDirection="column" display="flex">
                            <Box sx={{ margin: "auto" }}>
                                <IconButton disabled={filter.locked} data-id={id} onClick={handleClickDeleteFilter}>
                                    <Close />
                                </IconButton>
                            </Box>
                            <Box sx={{ margin: "auto" }}>
                                <IconButton onClick={handleToggleMember(id, "inactive")}>
                                    {filter.inactive ? <VisibilityOff/> : <Visibility/>}
                                </IconButton>
                            </Box>
                            <Box sx={{ margin: "auto" }}>
                                <IconButton onClick={handleToggleMember(id, "locked")}>
                                    {filter.locked ? <Lock/> : <LockOpen/>}
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                </Form>
                <Divider />
            </Box>
        })}
        <Button className={classes.addButton} variant={"contained"} onClick={handleClickAddFilter}>
            Add Filter
        </Button>
        {
            (Object.keys(filterModel.items).length > 1) &&
            <TextFieldAuto variant={"filled"} size={"small"} margin={"dense"}
                select
                label={"Link"}
                className={classes.linkOperator}
                SelectProps={{
                    value: filterModel.linkOperator ?? GridLinkOperator.And,
                    onChange: (e) =>
                    {
                        set(`sheets/${project}/${sheetId}/filterModel/linkOperator`, e.target.value);
                    }
                }}>
                <MenuItem value={GridLinkOperator.And}>{"And"}</MenuItem>
                <MenuItem value={GridLinkOperator.Or}>{"Or"}</MenuItem>
            </TextFieldAuto>
        }
    </Box >
}

const mapStateToProps = (store: never) => ({
    project: selectProject(store),
    modeData: selectModeData(store) ?? {} as ISheetModeData,
    sheets: selectSheets(store) ?? {},
    constants: selectConstants(store) ?? {}
});
export default connect(mapStateToProps, {
})(SheetFilters);